import { useState, useEffect, useCallback, useMemo } from "react";

/**
 * 尺寸类型常量
 */
export const SIZE_TYPES = {
  MOBILE: "mobile",
  TABLET_PORTRAIT: "tablet_portrait",
  TABLET_LANDSCAPE: "tablet_landscape",
};

/**
 * 获取当前尺寸类型
 * @returns {string} 尺寸类型
 */
export const getCurrentSizeType = () => {
  const width = window.innerWidth;
  const height = window.innerHeight;

  // 小于768px为手机版
  if (width < 768) {
    return SIZE_TYPES.MOBILE;
  }

  // 大于等于768px时，判断横竖屏
  return width >= height
    ? SIZE_TYPES.TABLET_LANDSCAPE
    : SIZE_TYPES.TABLET_PORTRAIT;
};

/**
 * 获取尺寸信息对象
 * @returns {object} 包含尺寸类型和详细信息
 */
export const getSizeInfo = () => {
  const sizeType = getCurrentSizeType();
  const width = window.innerWidth;
  const height = window.innerHeight;

  return {
    sizeType,
    width,
    height,
    isMobile: sizeType === SIZE_TYPES.MOBILE,
    isTabletPortrait: sizeType === SIZE_TYPES.TABLET_PORTRAIT,
    isTabletLandscape: sizeType === SIZE_TYPES.TABLET_LANDSCAPE,
    isTablet:
      sizeType === SIZE_TYPES.TABLET_PORTRAIT ||
      sizeType === SIZE_TYPES.TABLET_LANDSCAPE,
  };
};

/**
 * React Hook: 监听尺寸变化
 * @returns {object} 包含尺寸信息
 */
export const useSizeDetector = () => {
  // 立即获取初始值，避免闪烁
  const [sizeInfo, setSizeInfo] = useState(() => getSizeInfo());

  // 防抖更新函数
  const updateSizeInfo = useCallback(() => {
    const newSizeInfo = getSizeInfo();
    setSizeInfo((prevInfo) => {
      // 只有尺寸类型真正改变时才更新，减少重渲染
      if (prevInfo.sizeType !== newSizeInfo.sizeType) {
        return newSizeInfo;
      }
      return prevInfo;
    });
  }, []);

  useEffect(() => {
    let timeoutId;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateSizeInfo, 150);
    };

    const handleOrientationChange = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateSizeInfo, 300);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleOrientationChange);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, [updateSizeInfo]);

  return sizeInfo;
};

/**
 * 组件选择器Hook
 * @param {object} componentMap - 组件映射对象
 * @returns {React.Component} 当前尺寸对应的组件
 */
export const useResponsiveComponent = (componentMap) => {
  const { sizeType } = useSizeDetector();
  return useMemo(() => {
    return componentMap[sizeType] || componentMap[SIZE_TYPES.MOBILE] || null;
  }, [sizeType, componentMap]);
};

/**
 * 高阶组件：创建响应式组件
 * @param {object} components - 包含不同尺寸组件的对象
 * @returns {React.Component}
 */
export const createResponsiveComponent = (components) => {
  return (props) => {
    const { sizeType } = useSizeDetector();

    const Component = components[sizeType] || components[SIZE_TYPES.MOBILE];
    return Component ? <Component {...props} /> : null;
  };
};

/**
 * 条件渲染Hook
 * @returns {object} 包含条件判断的对象
 */
export const useSizeConditions = () => {
  const { sizeType } = useSizeDetector();

  return useMemo(
    () => ({
      sizeType,
      when: {
        mobile: (component) =>
          sizeType === SIZE_TYPES.MOBILE ? component : null,
        tabletPortrait: (component) =>
          sizeType === SIZE_TYPES.TABLET_PORTRAIT ? component : null,
        tabletLandscape: (component) =>
          sizeType === SIZE_TYPES.TABLET_LANDSCAPE ? component : null,
        tablet: (component) =>
          sizeType === SIZE_TYPES.TABLET_PORTRAIT ||
          sizeType === SIZE_TYPES.TABLET_LANDSCAPE
            ? component
            : null,
        not: {
          mobile: (component) =>
            sizeType !== SIZE_TYPES.MOBILE ? component : null,
          tabletPortrait: (component) =>
            sizeType !== SIZE_TYPES.TABLET_PORTRAIT ? component : null,
          tabletLandscape: (component) =>
            sizeType !== SIZE_TYPES.TABLET_LANDSCAPE ? component : null,
          tablet: (component) =>
            sizeType === SIZE_TYPES.MOBILE ? component : null,
        },
      },
    }),
    [sizeType],
  );
};

/**
 * 自定义断点检测Hook
 * @param {number} breakpoint - 自定义断点
 * @returns {object} 包含断点检测结果
 */
export const useCustomBreakpoint = (breakpoint = 768) => {
  const { width } = useSizeDetector();

  return useMemo(
    () => ({
      isBelowBreakpoint: width < breakpoint,
      isAboveBreakpoint: width >= breakpoint,
      width,
    }),
    [width, breakpoint],
  );
};

/**
 * 媒体查询样式Hook（可选，用于特殊情况）
 * @returns {object} 包含样式类名的对象
 */
export const useSizeClasses = () => {
  const { sizeType } = useSizeDetector();

  return useMemo(
    () => ({
      sizeType,
      mobile: sizeType === SIZE_TYPES.MOBILE,
      tabletPortrait: sizeType === SIZE_TYPES.TABLET_PORTRAIT,
      tabletLandscape: sizeType === SIZE_TYPES.TABLET_LANDSCAPE,
      tablet:
        sizeType === SIZE_TYPES.TABLET_PORTRAIT ||
        sizeType === SIZE_TYPES.TABLET_LANDSCAPE,
      // 生成CSS类名
      className: `size-${sizeType}`,
      // 条件类名生成器
      getClassName: (baseClass) => `${baseClass} ${baseClass}--${sizeType}`,
    }),
    [sizeType],
  );
};

/**
 * 监听单次尺寸变化Hook（性能优化版，用于只需要监听一次变化的场景）
 * @param {function} callback - 尺寸变化时的回调函数
 * @param {array} deps - 依赖数组
 */
export const useSizeChangeEffect = (callback, deps = []) => {
  const { sizeType } = useSizeDetector();
  const prevSizeTypeRef = useRef(sizeType);

  useEffect(() => {
    if (prevSizeTypeRef.current !== sizeType) {
      callback(sizeType, prevSizeTypeRef.current);
      prevSizeTypeRef.current = sizeType;
    }
  }, [sizeType, callback, ...deps]);
};

/**
 * 工具函数：检查是否为移动设备
 * @returns {boolean}
 */
export const isMobileDevice = () => {
  return getCurrentSizeType() === SIZE_TYPES.MOBILE;
};

/**
 * 工具函数：检查是否为平板设备
 * @returns {boolean}
 */
export const isTabletDevice = () => {
  const sizeType = getCurrentSizeType();
  return (
    sizeType === SIZE_TYPES.TABLET_PORTRAIT ||
    sizeType === SIZE_TYPES.TABLET_LANDSCAPE
  );
};

/**
 * 工具函数：检查是否为横屏
 * @returns {boolean}
 */
export const isLandscape = () => {
  return window.innerWidth >= window.innerHeight;
};

/**
 * 工具函数：检查是否为竖屏
 * @returns {boolean}
 */
export const isPortrait = () => {
  return window.innerWidth < window.innerHeight;
};

/**
 * 创建响应式样式对象Hook
 * @param {object} styleMap - 样式映射对象
 * @returns {object} 当前尺寸对应的样式对象
 */
export const useResponsiveStyles = (styleMap) => {
  const { sizeType } = useSizeDetector();

  return useMemo(() => {
    const baseStyles = styleMap.base || {};
    const sizeStyles = styleMap[sizeType] || {};
    return { ...baseStyles, ...sizeStyles };
  }, [sizeType, styleMap]);
};

/**
 * 响应式值Hook（根据尺寸返回不同的值）
 * @param {object} valueMap - 值映射对象
 * @returns {any} 当前尺寸对应的值
 */
export const useResponsiveValue = (valueMap) => {
  const { sizeType } = useSizeDetector();

  return useMemo(() => {
    return (
      valueMap[sizeType] || valueMap[SIZE_TYPES.MOBILE] || valueMap.default
    );
  }, [sizeType, valueMap]);
};
